import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  Pagination,
  DatePicker,
  Select,
  Tag,
  Space,
  Typography,
} from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { debounce } from "lodash";

import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useSuccessErrorModal from "../../hooks/successErrorModal/useSuccessErrorModal";
import EndpointFunctions from "../../utils/Functions/endpointFunctions";
import Endpoints from "../../api/endpoints";
import routes from "../../utils/routes";

const { RangePicker } = DatePicker;
const { Link } = Typography;

const TYPE_OPTIONS = [
  { label: "Hepsi", value: null },
  { label: "Damga", value: "STAMP" },
  { label: "Ödül", value: "REWARD" },
];

const formatDateTime = (iso) =>
  iso ? dayjs(iso).format("DD.MM.YYYY HH:mm:ss") : "-";

const TypeTag = ({ type }) => {
  if (type === "STAMP") return <Tag color="green">Damga</Tag>;
  if (type === "REWARD") return <Tag color="gold">Ödül</Tag>;
  return <Tag>{type}</Tag>;
};

const RoleTag = ({ role }) => {
  if (role === "SHOP_OFFICER") return <Tag color="blue">Sahip</Tag>;
  if (role === "SHOP_PERSONNEL") return <Tag>Personel</Tag>;
  return <Tag>{role}</Tag>;
};

const Transactions = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const { showModal, closeModal, ModalComponent } = useSuccessErrorModal();

  const initialRange = [dayjs().subtract(7, "day").startOf("day"), dayjs().endOf("day")];

  const [dateRange, setDateRange] = useState(initialRange);
  const [filter, setFilter] = useState({
    page: 0,
    size: 20,
    total: 0,
    pageCount: 0,
    startDate: initialRange[0].toISOString(),
    endDate: initialRange[1].toISOString(),
    shopId: null,
    transactionType: null,
  });

  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  const [shopOptions, setShopOptions] = useState([]);
  const [shopSearchLoading, setShopSearchLoading] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchTransactions(controller);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filter.page,
    filter.size,
    filter.startDate,
    filter.endDate,
    filter.shopId,
    filter.transactionType,
  ]);

  const fetchTransactions = async (controller) => {
    setTableLoading(true);

    const httpParams = {
      page: filter.page,
      size: filter.size,
    };
    if (filter.startDate) httpParams.startDate = filter.startDate;
    if (filter.endDate) httpParams.endDate = filter.endDate;
    if (filter.shopId) httpParams.shopId = filter.shopId;
    if (filter.transactionType)
      httpParams.transactionType = filter.transactionType;

    const result = await EndpointFunctions.fetchData({
      endpointURL: Endpoints.getAllTransactions,
      axiosPrivate: axiosPrivate,
      httpParams,
      controller,
    });

    if (controller.signal.aborted) return;

    if (result.success) {
      const items = result?.data?.data ?? [];
      setTableData(items.map((item) => ({ ...item, key: item.id })));
      setFilter((prev) => ({
        ...prev,
        total: result?.data?.total ?? 0,
        size: result?.data?.pageSize ?? prev.size,
        pageCount: result?.data?.pageCount ?? 0,
      }));
    } else {
      showModal(result.errorTitle, result.errorMessage, () => closeModal());
    }
    setTableLoading(false);
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    setFilter((prev) => ({
      ...prev,
      startDate: dates?.[0] ? dates[0].startOf("day").toISOString() : null,
      endDate: dates?.[1] ? dates[1].endOf("day").toISOString() : null,
      page: 0,
    }));
  };

  const handleTypeChange = (value) => {
    setFilter((prev) => ({ ...prev, transactionType: value ?? null, page: 0 }));
  };

  const handleShopChange = (value) => {
    setFilter((prev) => ({ ...prev, shopId: value ?? null, page: 0 }));
  };

  const searchShops = async (value) => {
    if (!value) {
      setShopOptions([]);
      return;
    }
    setShopSearchLoading(true);
    const result = await EndpointFunctions.fetchData({
      endpointURL: Endpoints.searchShop,
      axiosPrivate,
      httpParams: { page: 0, size: 20, searchString: value },
    });
    if (result.success) {
      const items = result?.data?.data ?? [];
      setShopOptions(
        items.map((shop) => ({ label: shop.name, value: shop.id }))
      );
    }
    setShopSearchLoading(false);
  };

  const debouncedShopSearch = useCallback(debounce(searchShops, 400), []);

  const paginationChange = (page, pageSize) => {
    setFilter((prev) => ({ ...prev, page: page - 1, size: pageSize }));
  };

  const columns = [
    {
      title: "Tarih",
      dataIndex: "transactionDate",
      key: "transactionDate",
      render: formatDateTime,
      width: 180,
    },
    {
      title: "Dükkan",
      dataIndex: "shopName",
      key: "shopName",
      render: (name, record) => (
        <Link
          onClick={(e) => {
            e.stopPropagation();
            navigate(`${routes.shopDetails}/${record.shopId}`);
          }}>
          {name || "-"}
        </Link>
      ),
    },
    {
      title: "Müşteri",
      dataIndex: "clientName",
      key: "clientName",
      render: (name, record) =>
        record.clientId ? (
          <Link
            onClick={(e) => {
              e.stopPropagation();
              navigate(`${routes.clientDetails}/${record.clientId}`);
            }}>
            {name || "-"}
          </Link>
        ) : (
          <span style={{ color: "#999" }}>Anonim</span>
        ),
    },
    {
      title: "Tip",
      dataIndex: "transactionType",
      key: "transactionType",
      width: 120,
      render: (type) => <TypeTag type={type} />,
    },
    {
      title: "İşlemi Yapan",
      key: "executedBy",
      render: (_, record) => (
        <Space>
          <span>{record.executedByName || "-"}</span>
          <RoleTag role={record.executedByRole} />
        </Space>
      ),
    },
    {
      title: "Kart",
      dataIndex: "cardLabel",
      key: "cardLabel",
      render: (label) => label || "-",
    },
  ];

  return (
    <>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 16,
          marginBottom: 20,
        }}>
        <RangePicker
          value={dateRange}
          onChange={handleDateChange}
          format="DD.MM.YYYY"
          allowClear
          size="large"
        />
        <Select
          showSearch
          allowClear
          placeholder="Dükkan filtrele"
          style={{ minWidth: 260 }}
          size="large"
          filterOption={false}
          onSearch={debouncedShopSearch}
          onChange={handleShopChange}
          options={shopOptions}
          loading={shopSearchLoading}
          notFoundContent={shopSearchLoading ? "Aranıyor..." : null}
        />
        <Select
          placeholder="Tip"
          style={{ minWidth: 140 }}
          size="large"
          options={TYPE_OPTIONS}
          defaultValue={null}
          onChange={handleTypeChange}
        />
      </div>

      <Table
        sticky
        pagination={false}
        dataSource={tableData}
        columns={columns}
        scroll={{ x: "max-content" }}
        loading={tableLoading}
      />

      <div style={{ height: 20 }} />
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Pagination
          onChange={paginationChange}
          total={filter.total}
          showTotal={(total, range) =>
            `${range[0]}-${range[1]} / toplam ${total} işlem`
          }
          pageSize={filter.size}
          current={filter.page + 1}
          showSizeChanger
          pageSizeOptions={[20, 50, 100]}
        />
      </div>
      <div style={{ height: 20 }} />
      <ModalComponent />
    </>
  );
};

export default Transactions;
